import { AppBar, FormControlLabel, IconButton, Snackbar, Switch, Tooltip, makeStyles, styled, withStyles } from "@material-ui/core";
import GetAppIcon from '@material-ui/icons/GetApp';
import { Linked, useIO, useLinked, useModel } from "@type-r/react";
import { Button, Column, Columns, Flex, Input, Loading, Page, PopupDialog, PrimaryButton, SelectSortBy, TilesList, Title, Toolbar, div, span } from "lqx-react";
import * as React from "react";
import { useEffect, useMemo } from "react";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";
import { Can } from "src/app/services/can";
import { AssetsVaultCard } from "../api/assetsVaultCard";
import { InvoiceVaultCard } from "../api/invoiceVaultCard";
import { Row } from "../components/item-card-styles";
import { AddFilter } from "../components/search-bar/search-bar";
import { CountListSection } from "../count-list-section";
import { DigiCheckbox, downloadInvoiceCsvReportFile, preventDefaultAndStopPropagation } from "../shared";
import { InvoiceDetailsPane } from "./invoice-details-pane";
import { InvoiceVaultCardRenderer } from "./invoice-vault-card-renderer";
import { SortOptions } from "./models/invoices-filters";
import { InvoicesStore } from "./models/invoices-store";
import { PopupActionDialog } from "../components/popups/popup-action-dialog";
import { InvoiceUnarchive } from "../api/invoiceUnarchive";
import { Alert } from "@material-ui/lab";
import { MultiSelectDropdown } from "../connections/components/multiselect-dropdown";
import { getFacilities } from "../api/facilities";
import { InvoiceBulkDelete } from "../api/invoiceBulkDelete";

export const InvoicesPage = () => {
    var classes = useStyles();
    const assetsVaultCard = useModel(AssetsVaultCard);
    const $activeInvoiceCard: Linked<InvoiceVaultCard> = useLinked<InvoiceVaultCard>(null);
    const state = useModel(InvoicesStore);
    const [isReady, setIsReady] = React.useState(false);
    const $selectedInvoiceDetailsTab = useLinked('details');
    const [invoicesVaultCard, setInvoicesVaultCard] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [countAll, setCountAll] = React.useState(0);
    const $isUploadPopUp = useLinked(false);
    const $dateFrom = useLinked(new Date());
    const $dateTo = useLinked(new Date());
    const $doUnarchive = useLinked<{ assetUids: string[], needApproval?: boolean, selectAllByFilters?: boolean }>({ assetUids: [], needApproval: null });
    const $doDelete = useLinked<{ assetUids: string[], needApproval?: boolean, selectAllByFilters?: boolean }>({ assetUids: [], needApproval: null });

    const unarchiveModel = useModel(InvoiceUnarchive);
    const bulkDeleteModel = useModel(InvoiceBulkDelete);

    const defaultContext = { onApply: null, onCancel: null, actionType: 'Unarchive & Send', actionText: 'Are you sure you want to unarchive asset and send it to SCF?', isPopupOpen: false };
    const defaultBulkDeleteContext = { onApply: null, onCancel: null, actionType: 'Bulk Delete', actionText: 'Are you sure you want to bulk delete assets? This action CANNOT be undone.', isPopupOpen: false };

    const $popupContext = useLinked<{
        onApply: any,
        onCancel: any,
        actionType: String,
        actionText: String,
        isPopupOpen: boolean,
    }>(defaultContext);

    const $popupBulkDeleteContext = useLinked<{
        onApply: any,
        onCancel: any,
        actionType: String,
        actionText: String,
        isPopupOpen: boolean,
    }>(defaultBulkDeleteContext);

    const $displaySuccessMessage = useLinked<{ showMessageFlag: boolean, message: string }>({ showMessageFlag: false, message: '' });
    const $selectAllToUnarchive = useLinked<{ shouldTriggerOnChange: boolean, flagCheckbox: boolean }>({ shouldTriggerOnChange: true, flagCheckbox: false }).onChange(event => {
        if (!event.shouldTriggerOnChange && !event.flagCheckbox) {
            event.shouldTriggerOnChange = true;
            return false;
        }
        invoicesVaultCard.forEach(item => { item.checked = event.flagCheckbox });
        setInvoicesVaultCard(invoicesVaultCard);
    });
    const [selectedFacilities, setSelectedFacilities] = React.useState<any>();

    const onCancel = () => {
        $doUnarchive.set({ assetUids: [], needApproval: null });
        $popupContext.set(defaultContext);
        setSelectedFacilities([]);
    }

    const onApply = () => {
        $doUnarchive.set({ ...$doUnarchive.value, needApproval: false });
        $popupContext.set(defaultContext);
    }

    const onCancelDeleteAction = () => {
        $doDelete.set({ assetUids: [], needApproval: null });
        $popupBulkDeleteContext.set(defaultBulkDeleteContext);
    }

    const onApplyDeleteAction = () => {
        $doDelete.set({ ...$doDelete.value, needApproval: false });
        $popupBulkDeleteContext.set(defaultBulkDeleteContext);
    }

    const handleCloseShowSuccessfulUnarchiveMessage = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        $displaySuccessMessage.set({ showMessageFlag: false, message: '' });
    };

    useIO(async () => {
        await getData();
        setInvoicesVaultCard(assetsVaultCard.invoicesVaultCard.items.models);
        setCountAll(assetsVaultCard.invoicesVaultCard.count);
        setIsReady(false);
        state.initialize()
        setIsReady(true);
    }, [])

    useIO(async () => {
        if (Boolean($doUnarchive.value.assetUids.length) && $doUnarchive.value.needApproval !== null) {
            if ($doUnarchive.value.needApproval) {
                if ($doUnarchive.value.assetUids.length > 1) {
                    $popupContext.set({
                        ...$popupContext.value,
                        onApply: () => onApply(),
                        onCancel: () => onCancel(),
                        isPopupOpen: true,
                        actionText: 'Are you sure you want to unarchive selected ' + invoicesVaultCard.filter(x => x.checked).length + ' assets and send them to SCF?'
                    });
                } else {
                    $popupContext.set({
                        ...$popupContext.value,
                        onApply: () => onApply(),
                        onCancel: () => onCancel(),
                        isPopupOpen: true
                    });
                }
            } else {
                if ($doUnarchive.value.assetUids.length > 1) {
                    setIsReady(false);
                    const assetIds = invoicesVaultCard.filter(x => x.checked).map(item => item.uid);
                    await doUnarchive(assetIds);
                    setInvoicesVaultCard(state.isArchived ? invoicesVaultCard.filter(x => x.isArchived) : invoicesVaultCard);
                    $selectAllToUnarchive.set({ shouldTriggerOnChange: true, flagCheckbox: false });
                    await getData();
                    setInvoicesVaultCard(assetsVaultCard.invoicesVaultCard.items.models);
                    setIsReady(true);
                } else {
                    const assetIds = $doUnarchive.value.assetUids[0];

                    await doUnarchive([assetIds]);
                    setInvoicesVaultCard(state.isArchived ? assetsVaultCard.invoicesVaultCard.items.models.filter(x => x.isArchived) : assetsVaultCard.invoicesVaultCard.items.models);
                }
            }
        }

    }, [$doUnarchive.value]);

    useIO(async () => {
        if (Boolean($doDelete.value.assetUids.length) && $doDelete.value.needApproval !== null) {
            if ($doDelete.value.needApproval) {
                if ($doDelete.value.assetUids.length > 1) {
                    $popupBulkDeleteContext.set({
                        ...$popupBulkDeleteContext.value,
                        onApply: () => onApplyDeleteAction(),
                        onCancel: () => onCancelDeleteAction(),
                        isPopupOpen: true,
                        actionText: 'Are you sure you want to bulk delete ' + invoicesVaultCard.filter(x => x.checked).length + ' assets? This action CANNOT be undone.'
                    });
                } else {
                    $popupBulkDeleteContext.set({
                        ...$popupBulkDeleteContext.value,
                        actionText: 'Are you sure you want to bulk delete assets? This action CANNOT be undone.',
                        onApply: () => onApplyDeleteAction(),
                        onCancel: () => onCancelDeleteAction(),
                        isPopupOpen: true
                    });
                }
            } else {
                // if ($doDelete.value.assetUids.length > 1) {
                    setIsReady(false);
                    const assetIds = invoicesVaultCard.filter(x => x.checked).map(item => item.uid);
                    await doBulkDelete(assetIds);
                    setInvoicesVaultCard(state.isArchived ? invoicesVaultCard.filter(x => x.isArchived) : invoicesVaultCard);
                    $selectAllToUnarchive.set({ shouldTriggerOnChange: true, flagCheckbox: false });
                    await getData();
                    setInvoicesVaultCard(assetsVaultCard.invoicesVaultCard.items.models);
                    setIsReady(true);
                // } else {
                //     const assetIds = $doDelete.value.assetUids[0];

                //     await doBulkDelete([assetIds]);
                //     setInvoicesVaultCard(state.isArchived ? assetsVaultCard.invoicesVaultCard.items.models.filter(x => x.isArchived) : assetsVaultCard.invoicesVaultCard.items.models);
                // }
            }
        }

    }, [$doDelete.value]);

    const doUnarchive = async (assetIds) => {
        try {
            if (assetIds.length) {
                await unarchiveModel.do(assetIds, selectedFacilities);
                let currentItems = invoicesVaultCard.filter(x => assetIds.includes(x.uid));
                let currentItemNumbers = currentItems.map(item => item.dataset.number);
                let unarchiveSuccessMessage = {
                    message: assetIds.length > 1 ?
                        `${assetIds.length} Invoices were successfully unarchived`
                        : `Invoice with id - ${currentItemNumbers[0]} was successfully unarchived`,
                    showMessageFlag: true
                }
                $displaySuccessMessage.set(unarchiveSuccessMessage)
                let archivedAssets = assetsVaultCard.invoicesVaultCard.items.models.filter(x => $doUnarchive.value.assetUids.includes(x.uid));
                archivedAssets.forEach(item => { item.isArchived = false });
                if (selectedFacilities) {
                    setSelectedFacilities([]);
                }
                if (state.isArchived) {
                    assetsVaultCard.invoicesVaultCard.count = assetsVaultCard.invoicesVaultCard.count - assetIds.length;
                }
            }
        }
        finally {
            $doUnarchive.set({ ...$doUnarchive.value, needApproval: null });
        }
    }

    const doBulkDelete = async (assetIds) => {
        try {
            if (assetIds.length) {
                await bulkDeleteModel.do(assetIds);
                let currentItems = invoicesVaultCard.filter(x => assetIds.includes(x.uid));
                let currentItemNumbers = currentItems.map(item => item.dataset.number);
                let bulkDeleteSuccessMessage = {
                    message: assetIds.length > 1 ?
                        `${assetIds.length} Invoices were successfully deleted`
                        : `Invoice with id - ${currentItemNumbers[0]} was successfully deleted`,
                    showMessageFlag: true
                }
                $displaySuccessMessage.set(bulkDeleteSuccessMessage)
                let deletedAssets = assetsVaultCard.invoicesVaultCard.items.models.filter(x => $doUnarchive.value.assetUids.includes(x.uid));
                deletedAssets.forEach(item => { item.isArchived = false });
                if (state.isArchived) {
                    assetsVaultCard.invoicesVaultCard.count = assetsVaultCard.invoicesVaultCard.count - assetIds.length;
                }
            }
        }
        finally {
            $doDelete.set({ ...$doDelete.value, needApproval: null });
        }
    }

    const getData = async (skip = 0, count = 100) => {
        const params = state.mapToQuery(skip, count);
        setIsLoading(true)
        await assetsVaultCard.invoicesVaultCard.fetch({ params: params })
            .then(c => c.items.map(i => i.dataset.onLoaded()))
            .catch(e => console.error(e));
        setIsLoading(false);
        setIsReady(true);
    };

    const getItems = React.useCallback(async (searchText) => {
        return (await getFacilities(searchText));
    }, []);

    useMemo(async () => {
        if (isReady) {
            setIsReady(false);
            $activeInvoiceCard.set(null);
            await getData();
            setInvoicesVaultCard(assetsVaultCard.invoicesVaultCard.items.models);
        }
    }, [state.filter['_changeToken'], state.isArchived]);

    useEffect(() => {
        if ($selectAllToUnarchive.value.flagCheckbox && invoicesVaultCard.some(x => !x.checked)) {
            $selectAllToUnarchive.set({ shouldTriggerOnChange: false, flagCheckbox: false });
        }
        if (!$selectAllToUnarchive.value.flagCheckbox && invoicesVaultCard.length > 0 && invoicesVaultCard.every(x => x.checked)) {
            $selectAllToUnarchive.set({ shouldTriggerOnChange: false, flagCheckbox: true });
        }
    }, [assetsVaultCard['_changeToken']]);

    const closeDetailsPane = () => $activeInvoiceCard.set(null);

    const onVaultCardClicked = vaultCard => {
        $selectedInvoiceDetailsTab.set('details');
    }

    const handleDataOnScroll = async () => {
        if (!isLoading && assetsVaultCard.invoicesVaultCard.items.length === 100) {
            await getData(invoicesVaultCard.length);
            setInvoicesVaultCard(prev => [...invoicesVaultCard, ...assetsVaultCard.invoicesVaultCard.items.models]);
        }
    }

    const scrollEvent = (handle: () => void) => ({ target }) => {
        const isBottom = target.scrollHeight - target.offsetHeight - target.scrollTop <= 1 && target.scrollHeight / target.offsetHeight > 2.1;
        if (isBottom) {
            handle();
        }
    }

    const download = async (e: any, url: string, dateFrom: Date, dateTo: Date) => {
        downloadInvoiceCsvReportFile(url, dateFrom, dateTo);
    }

    const canclePopUp = () => {
        $isUploadPopUp.set(false);
        $dateFrom.set(new Date());
        $dateTo.set(new Date());
    }

    return <>
        <Page maxWidth={false} header={
            <Flex center>
                <h4 className={classes.title}>Invoices</h4>
            </Flex>
        }
            top={
                <AppBar position="static" color="secondary">
                    <Toolbar>
                        <StyledFilterBar filter={state.filter} filterAllPlaceholder="Filter by Supplier Name, Buyer Name, Invoice Number or Face Value" />
                        {/* <StateFilter stateValue={state.filter.$.stateId} filterOptions={InvoiceStateOptions} /> */}
                        <StyledFormControlLabel
                            control={
                                <Switch
                                    onChange={(e, c) => state.isArchived = c}
                                    color="primary"
                                />
                            }
                            label={"Archived"}
                            labelPlacement="start"
                        />
                        <SelectSortBy $attr={state.filter.sortBy.$.attr} $asc={state.filter.sortBy.$.asc} options={SortOptions} />

                        <StyledIconButton aria-label="Download" size="small"
                            onClick={() => { $isUploadPopUp.set(true) }}>
                            <GetAppIcon titleAccess="Download invoice report" />
                        </StyledIconButton>
                    </Toolbar>
                </AppBar>
            }>
            {isReady ?
                <Columns style={{ minHeight: 'calc(100% - 80px)' }}>
                    <TilesListColumn onScroll={scrollEvent(handleDataOnScroll)}>
                        {
                            state.isArchived &&
                            <UnarchiveBlock>
                                <DigiCheckbox $checked={$selectAllToUnarchive.$.flagCheckbox} label={"Select All"} readOnly={invoicesVaultCard.length === 0} />
                                {
                                    Can(UserEntitlementCatalogs.UnarchiveInvoice) &&
                                    <SelectedAllUnarchiveButton color="primary"
                                        //disabled={invoicesVaultCard.filter(x => x.checked).length === 0}
                                        onClick={
                                            (evt) => {
                                                preventDefaultAndStopPropagation(evt);
                                                $doUnarchive.set({ assetUids: invoicesVaultCard.filter(x => x.checked).map(item => item.uid), needApproval: true });
                                            }
                                        }>Bulk Unarchive</SelectedAllUnarchiveButton>
                                }
                                {
                                    Can(UserEntitlementCatalogs.BulkDeleteInvoices) &&
                                    <SelectedAllUnarchiveButton color="primary"
                                        //disabled={invoicesVaultCard.filter(x => x.checked).length === 0}
                                        onClick={
                                            (evt) => {
                                                preventDefaultAndStopPropagation(evt);
                                                $doDelete.set({ assetUids: invoicesVaultCard.filter(x => x.checked).map(item => item.uid), needApproval: true });
                                            }
                                        }>Bulk Delete</SelectedAllUnarchiveButton>
                                }

                            </UnarchiveBlock>
                        }

                        <CountListSection countAll={countAll} countFiltered={assetsVaultCard.invoicesVaultCard.count}></CountListSection>
                        <TilesList
                            items={invoicesVaultCard}
                            itemSize={100}
                            renderer={InvoiceVaultCardRenderer($activeInvoiceCard, $doUnarchive, state.isArchived, (vaultCard: InvoiceVaultCard, isUnarchiving) => {
                                return (
                                    <>
                                        {
                                            !$activeInvoiceCard.value && Can(UserEntitlementCatalogs.EditVaultInvoicesPage) && <EditButton onClick={evt => {
                                                $selectedInvoiceDetailsTab.set("edit");
                                                $activeInvoiceCard.set(vaultCard);
                                                evt.stopPropagation();
                                            }} color="primary">Edit</EditButton>
                                        }
                                        {
                                            vaultCard.dataset.isDigitized &&
                                            <Tooltip title={vaultCard.isArchived ? <span style={{ fontSize: "12px" }}>Can't send archived invoice</span> : ""}>
                                                <ButtonTooltipBlock>
                                                    <SendToButton color="primary"
                                                        disabled={vaultCard.isArchived}
                                                        onClick={
                                                            evt => {
                                                                $selectedInvoiceDetailsTab.set("sendTo");
                                                                $activeInvoiceCard.set(vaultCard);
                                                                evt.stopPropagation();
                                                            }
                                                        }>Send To</SendToButton>
                                                </ButtonTooltipBlock>
                                            </Tooltip>

                                        }

                                        {
                                            vaultCard.isArchived && Can(UserEntitlementCatalogs.UnarchiveInvoice) &&
                                            vaultCard.dataset.isDigitized && <UnarchiveButton color="primary"
                                                disabled={isUnarchiving}
                                                onClick={
                                                    (evt) => {
                                                        preventDefaultAndStopPropagation(evt);
                                                        $doUnarchive.set({ assetUids: [vaultCard.uid], needApproval: true });
                                                    }
                                                }>Unarchive</UnarchiveButton>
                                        }
                                    </>
                                )
                            }, onVaultCardClicked)}
                            selected={undefined /* selected */}
                        />
                    </TilesListColumn>
                    {$activeInvoiceCard.value &&
                        <InvoiceDetailsPaneColumn>
                            <InvoiceDetailsPane close={closeDetailsPane} invoice={$activeInvoiceCard.value.dataset} invoiceId={$activeInvoiceCard.value.id} $selectedTab={$selectedInvoiceDetailsTab} invoiceData={$activeInvoiceCard.value} isArchived={$activeInvoiceCard.value.isArchived} />
                        </InvoiceDetailsPaneColumn>}
                </Columns> : <Loading />
            }
        </Page>

        {$isUploadPopUp.value ?
            <UploadSelectionPopupDialog
                title={<Title> Export to .csv </Title>}
                actions={
                    <>
                        <Button onClick={() => canclePopUp()}>Cancel</Button>
                        <PrimaryButton onClick={(e) => download(e, `/api/v1/assetmaster/assets/invoice-csv-file`, $dateFrom.value, $dateTo.value)}>Export</PrimaryButton>
                    </>
                }>
                <InformationText>Limited to 100,000 results</InformationText>
                <PopUpText>Date Range</PopUpText>
                <DateColumn>
                    <DateInput data-qa-id="date from" date $value={($dateFrom)} />
                </DateColumn>
                <DateColumn>
                    <DateInput data-qa-id="date to" date $value={($dateTo)} />
                </DateColumn>
            </UploadSelectionPopupDialog>
            : null}

        <PopupActionDialog $context={$popupContext} />
        {
            $popupContext.value.isPopupOpen ?
                <PopupDialog
                    title={$popupContext.value.actionType}
                    actions={
                        <>
                            <Button onClick={$popupContext.value.onCancel}>Cancel</Button>
                            <PrimaryButton onClick={$popupContext.value.onApply}>{$popupContext.value.actionType}</PrimaryButton>
                        </>
                    }
                    maxWidth='sm'
                    hideTitle={false}
                    onClose={$popupContext.value.actionType}>
                    {$popupContext.value.actionText}
                    {Can(UserEntitlementCatalogs.ManageTargetFacility) &&
                        <PickerContainer row>
                            <PickerLabel>
                                Facilities:
                            </PickerLabel>
                            <MultiSelectDropdown
                                styles={{ ...customFacilityStyles }}
                                isMultiple={true}
                                getItems={getItems}
                                selectedItems={selectedFacilities}
                                handleChange={(selectedItem) => { setSelectedFacilities(selectedItem) }}
                            />
                        </PickerContainer>
                    }
                </PopupDialog> : <></>
        }

        <PopupActionDialog $context={$popupBulkDeleteContext} />
        {
            $popupBulkDeleteContext.value.isPopupOpen ?
                <PopupDialog
                    title={$popupBulkDeleteContext.value.actionType}
                    actions={
                        <>
                            <Button onClick={$popupBulkDeleteContext.value.onCancel}>Cancel</Button>
                            <PrimaryButton onClick={$popupBulkDeleteContext.value.onApply}>{$popupBulkDeleteContext.value.actionType}</PrimaryButton>
                        </>
                    }
                    maxWidth='sm'
                    hideTitle={false}
                    onClose={$popupBulkDeleteContext.value.actionType}>
                    {$popupBulkDeleteContext.value.actionText}
                </PopupDialog> : <></>
        }

        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={$displaySuccessMessage.value.showMessageFlag} autoHideDuration={6000} onClose={handleCloseShowSuccessfulUnarchiveMessage}>
            <Alert severity="success">
                {$displaySuccessMessage.value.message}
            </Alert>
        </Snackbar>
    </>
};

const useStyles = makeStyles({
    title: {
        padding: '20px 0',
        color: '#3d6889',
        fontSize: '24px'
    }
});

const TilesListColumn = styled(Column)({
    minWidth: '500px',
    minHeight: '250px',
});

const InvoiceDetailsPaneColumn = styled(Column)({
    minWidth: '250px'
});

const SendToButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center'
});

const EditButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center'
});

const UnarchiveButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center'
});

const SelectedAllUnarchiveButton = styled(Button)({
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center',
    marginBottom: '6px'
});

const StyledFilterBar = styled(AddFilter)({
    width: '650px',
    alignItems: 'center',
    padding: '4px',
});

export const StyledIconButton = styled(IconButton)({
    margin: 0,
    padding: 0,
    fontSize: '20px'
})

const UploadSelectionPopupDialog = styled(PopupDialog)({
    '& .MuiDialog-paperFullWidth': {
        width: '580px',
        height: '210px'
    },
    '& .MuiDialog-paper': {
        overflowY: 'visible'
    }
});

const PopUpText = styled(Row)({
});

const InformationText = styled(Row)({
    color: 'red',
    fontSize: '12px',
    marginBottom: '5px'
});

export const DateInput = withStyles({
    input: {
        '&.date-picker': {
            zIndex: '1000',
            paddingRight: '23px',
            marginRight: '5px',
            backgroundOrigin: 'padding-box',
            backgroundPosition: 'right 5px bottom 10px',
            flex: 1
        }
    }

})(Input);

export const DateColumn = styled('span')({
    float: 'left',
    paddingRight: '50px'
});

const ButtonTooltipBlock = span({
});

const StyledFormControlLabel = withStyles({
    label: {
        fontSize: '13px',
        fontFamily: 'Montserrat',
        fontWeight: 500,
        color: 'grey',
    },
    labelPlacementStart: {
        margin: '0 !important'
    }
})(FormControlLabel)

const UnarchiveBlock = styled(Flex)({
    fontSize: '18px',
    marginBottom: '15px'
});

const PickerContainer = styled(Flex)({
    padding: '8px',
})

const PickerLabel = div({
    alignItems: 'center',
    display: 'flex',
    float: 'left',
    fontSize: "9pt",
    color: '#a4b6c6'
});

const customFacilityStyles = {
    float: "left",
    backgroundColor: 'white',
    width: '308px',
    marginLeft: '17px'
};